import { z } from 'zod';

/** GET /margens/ponto-equilibrio (so com custo.ver + rateio.ver): PE contabil, economico e financeiro da competencia do ultimo rateio FECHADO. */
export const PE_MESES = ['1', '3', '6', '12'] as const;
export const pontoEquilibrioQuerySchema = z.object({ meses: z.enum(PE_MESES).default('3') });
export type PontoEquilibrioQuery = z.infer<typeof pontoEquilibrioQuerySchema>;

export type OrigemMcPe = 'APROVADOS' | 'CARTEIRA' | 'LISTA_PUBLICADA';

export type PontoEquilibrioStatus = 'OK' | 'SEM_RATEIO_FECHADO' | 'SEM_VENDAS_APROVADAS' | 'MC_NAO_POSITIVA';

export interface PontoEquilibrioResponse {
  status: PontoEquilibrioStatus;
  /** Ex.: "PE indisponivel: feche o rateio de 2026-09". Null com status OK. */
  mensagem: string | null;
  /** Competencia (AAAA-MM) do rateio FECHADO usado, ou a que falta fechar. */
  competencia: string | null;
  /** Link para a tela de rateios ja na competencia. */
  link: { rota: string; busca: Record<string, string> } | null;
  /** Janela da MC% media: ultimos N meses ate o fim da competencia. */
  meses: number;
  periodo: { de: string; ate: string } | null;
  /** Gastos fixos da competencia (rateio fechado: CIF + salarios com encargos + depreciacoes) e a depreciacao dentro deles. */
  fixos: string | null;
  depreciacao: string | null;
  /** MC% media do mix dos orcamentos APROVADOS no periodo, ponderada pela receita sem IPI (nunca da lista de precos). */
  mc: { pct: string | null; origem: OrigemMcPe | null };
  /**
   * De onde veio a MC% quando nao ha vendas aprovadas na janela (Sessao 28): `CARTEIRA` = pedidos Concluido/Confirmado/Previsao do periodo;
   * `LISTA_PUBLICADA` = o mix da lista de precos publicada vigente (um de cada item, ponderado pelo preco sem IPI). Texto pronto para a tela.
   */
  baseDescricao: string | null;
  /** Mostrador (gauge): numeros prontos; as posicoes sao fracoes (0 a 1) da escala 0..maximo. Null sem PE. */
  gauge: {
    faturamento: string;
    pontoEquilibrio: string;
    mcMedia: string;
    margemSeguranca: string | null;
    maximo: string;
    posFaturamento: string;
    posPontoEquilibrio: string;
  } | null;
  /**
   * Aprovados na janela: `usados` entram na MC% (`deSnapshot` = os antigos cuja MC saiu do snapshot gravado, sem recalcular preco nem custo);
   * `foraDoCalculo` = aprovados sem o custo separado (materiais/aquisicao x processo) no snapshot, que NAO entram.
   */
  aprovados: { usados: number; deSnapshot: number; foraDoCalculo: number };
  /** Sem vendas aprovadas na janela mas com aprovados nos ultimos 12 meses: a MC% ponderada por esse periodo (rode com `meses=12`). */
  alternativa: { meses: 12; mcPct: string; aprovados: number } | null;
  /** Receita (sem IPI) da base usada no mes da competencia: aprovados ou, na carteira, os pedidos do mes (Previsao com o peso do plano). */
  receitaMes: string | null;
  lucroDesejado: string | null;
  pe: { contabil: string; economico: string; financeiro: string } | null;
  /** (receita - PE contabil) / receita; negativa = abaixo do equilibrio. Null sem receita. */
  margemSeguranca: string | null;
  despesaFixa: { real: string | null; parametro: string | null; diferenca: string | null; alerta: boolean };
}
